import { renderTabBar } from './tab-bar.js';
import { renderFloatingCompose } from './floating-compose.js';
import { renderSyncIndicator } from '../components/sync-indicator.js';
import { getState, subscribe, type TabId } from '../views.js';

type ViewRenderer = (root: HTMLElement) => (() => void) | void;

export function mountAppShell(root: HTMLElement, renderers: Record<TabId, ViewRenderer>) {
  root.innerHTML = `
    <div class="app-shell">
      <header class="app-shell__header">
        <div class="app-shell__brand">
          <img class="app-shell__logo" src="/icon-192.png" alt="" aria-hidden="true" />
          <span class="app-shell__brand-text">memori</span>
        </div>
        <span id="app-sync"></span>
      </header>
      <main class="app-shell__main" id="app-main"></main>
      <div id="app-compose"></div>
      <div id="app-tab-bar"></div>
    </div>
  `;
  const main = root.querySelector<HTMLElement>('#app-main')!;
  const composeEl = root.querySelector<HTMLElement>('#app-compose')!;
  const tabBarEl = root.querySelector<HTMLElement>('#app-tab-bar')!;
  const syncEl = root.querySelector<HTMLElement>('#app-sync')!;
  const header = root.querySelector<HTMLElement>('.app-shell__header')!;

  // Hide-on-scroll-down / show-on-scroll-up for header
  let lastScrollTop = 0;
  let ticking = false;
  const HEADER_HEIGHT = 48;
  const SCROLL_DELTA_THRESHOLD = 4;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const st = main.scrollTop;
      const delta = st - lastScrollTop;
      if (st <= HEADER_HEIGHT) {
        header.classList.remove('is-hidden');
      } else if (delta > SCROLL_DELTA_THRESHOLD) {
        header.classList.add('is-hidden');
      } else if (delta < -SCROLL_DELTA_THRESHOLD) {
        header.classList.remove('is-hidden');
      }
      lastScrollTop = st;
      ticking = false;
    });
  };
  main.addEventListener('scroll', onScroll, { passive: true });

  let cleanup: (() => void) | null = null;
  const renderActive = () => {
    if (cleanup) {
      cleanup();
      cleanup = null;
    }
    const { activeTab } = getState();
    main.innerHTML = '';
    const renderer = renderers[activeTab];
    const result = renderer(main);
    cleanup = typeof result === 'function' ? result : null;
  };

  renderActive();
  const unsubscribeTab = subscribe(renderActive);
  const cleanupCompose = renderFloatingCompose(composeEl);
  const cleanupTabBar = renderTabBar(tabBarEl);
  const cleanupSync = renderSyncIndicator(syncEl);

  return () => {
    if (cleanup) cleanup();
    cleanupCompose();
    cleanupTabBar();
    cleanupSync();
    unsubscribeTab();
    main.removeEventListener('scroll', onScroll);
  };
}
